import {
  Injectable,
  Inject,
  ApplicationRef,
  EnvironmentInjector,
  createComponent,
  ComponentRef,
  Type,
} from '@angular/core';
import { BaseModalComponent } from '../../shared/components/modal/base-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modalRef?: ComponentRef<any>;
  private modalQueue: Array<() => void> = [];
  private modalActive = false;

  constructor(
    @Inject(ApplicationRef) private appRef: ApplicationRef,
    @Inject(EnvironmentInjector) private injector: EnvironmentInjector
  ) {}

  /**
   * Generic method to show any modal component that extends BaseModalComponent
   * @param componentType The modal component class to create
   * @param configCallback Optional callback to configure the modal instance before showing
   * @returns Promise that resolves with the modal result, or undefined on cancel
   */
  showModalGeneric<T extends BaseModalComponent>(
    componentType: Type<T>,
    configCallback?: (instance: T) => void
  ): Promise<any> {
    return new Promise((resolve) => {
      const show = () => {
        this.modalActive = true;

        this.modalRef = createComponent(componentType, {
          environmentInjector: this.injector,
        });

        if (configCallback) {
          configCallback(this.modalRef.instance);
        }

        this.appRef.attachView(this.modalRef.hostView);
        const domElem = (this.modalRef.hostView as any).rootNodes[0] as HTMLElement;
        document.body.appendChild(domElem);
        this.modalRef.instance.show();

        const sub = this.modalRef.instance.onClose.subscribe((result: any) => {
          this.removeModal();
          sub.unsubscribe();
          this.modalActive = false;
          this.runNextInQueue();
          resolve(result);
        });
      };

      if (this.modalActive) {
        this.modalQueue.push(() => {
          show();
        });
      } else {
        show();
      }
    });
  }

  private runNextInQueue(): void {
    const next = this.modalQueue.shift();
    if (next) next();
  }

  show<T extends BaseModalComponent>(componentType: Type<T>, config?: any): Promise<any> {
    return this.showModalGeneric(componentType, (instance: any) => {
      if (instance.configure) {
        instance.configure(config);
      }
    });
  }

  private removeModal() {
    if (this.modalRef) {
      this.appRef.detachView(this.modalRef.hostView);
      this.modalRef.destroy();
      this.modalRef = undefined;
    }
  }
}
